import { store } from '@redux/store'
import _isEmpty from 'lodash/isEmpty'
import {
  setOrder,
  clearOrder,
  setCheckoutStep as setCheckoutStepRedux,
  setCheckoutStepLoading,
  initialState,
  setShippingInvalidFields,
  setDeclineModalInfo,
  setReviewInvalidFields,
} from '@redux/modules/checkout'
import { clearCart } from '@redux/modules/cart'
import { fetchAddressFormat, fetchAddressLookup, updateLineItems, updatePayment } from '@services/checkout'
import { announce } from '@helpers/aria-announce'
import { checkoutStepsOrIndex } from '@helpers/checkout/checkout'
import { scrollTo } from '@helpers/checkout/payment-section/digital-buy'
import { productPrice } from '@helpers/product'
import { validateShippingStep, setAddress, goBackToShippingStep } from './shipping-section'
import { validateDeliveryStep, canSkipDelivery } from './delivery-section'
import { validatePaymentStep } from './payment-section/payment-section'
import { redactCCFromOrder } from './payment-section/credit-card'
import { validateReviewStep, placeOrderReview } from './review-section'
import { checkoutStepAnalytics } from '../google-tag-manager'
import { getCurrentLocation, getRegionZone } from '../geo-location'
import { getCart } from '../cart'
import { submitBillingAddress } from './payment-section/billing-address'

export const CHECKOUT_STEPS = {
  shipping: 'shipping',
  delivery: 'delivery',
  payment: 'payment',
  review: 'review',
}

export const getOrder = () => store.getState().checkout.order
export const getDeliveryMode = () => store.getState().checkout.deliveryMode

export const setOrderInfo = (info, field, isNonFinanceCredit) => {
  const order = getOrder()
  const newOrder = {
    ...order,
    isNonFinanceCredit,
    [field]: info,
  }
  store.dispatch(setOrder(newOrder))
}

export const setOrderField = (field, value) => {
  const order = getOrder()
  const newOrder = {
    ...order,
    [field]: value,
  }
  store.dispatch(setOrder(newOrder))
}

// eslint-disable-next-line consistent-return
export const setCheckoutStep = async (e, checkoutStep, nextStep, placeOrder = false, authtoken) => {
  if (e) e.preventDefault()
  const checkoutStepIndex = checkoutStepsOrIndex(checkoutStep)
  const nextStepIndex = checkoutStepsOrIndex(nextStep)
  if (checkoutStepIndex < nextStepIndex) {
    announce(`${checkoutStep} Submitted`, '', '', 0)
  }
  store.dispatch(setCheckoutStepLoading(true))
  const invalidFields = await validateCheckoutStep(checkoutStep, nextStep)

  if (invalidFields.includes('buttonClick')) {
    store.dispatch(setCheckoutStepLoading(false))
    return invalidFields
  }

  if (!invalidFields || invalidFields.length < 1) {
    if (placeOrder) {
      await placeOrderReview(authtoken)
    } else if (checkoutStep === 'shipping' && nextStep === 'delivery') {
      const canSkip = await canSkipDelivery()
      if (canSkip) {
        store.dispatch(setCheckoutStepRedux('payment'))
        checkoutStepAnalytics('payment', canSkip)
      } else {
        store.dispatch(setCheckoutStepRedux('delivery'))
        checkoutStepAnalytics('delivery')
      }
    } else {
      store.dispatch(setCheckoutStepRedux(nextStep))
      checkoutStepAnalytics(nextStep)
    }
    if (typeof window !== 'undefined' && nextStep && checkoutStep !== nextStep) {
      const checkoutStepEl = document.querySelector(`#${nextStep}`)
      const checkoutStepHeaderEl = document.querySelector(`#Step${nextStepIndex}Header`)
      if (checkoutStepEl) {
        const topOfElement = checkoutStepEl.offsetTop - 85
        try {
          window.scroll({ top: topOfElement, behavior: 'smooth', block: 'start' })
          checkoutStepHeaderEl.focus()
        } catch {
          // do nothing
        }
      }
    }
  }
  store.dispatch(setCheckoutStepLoading(false))
}

export const validateCheckoutStep = (checkoutStep, nextStep) => {
  if (nextStep === 'shipping') {
    return goBackToShippingStep()
  }
  switch (checkoutStep) {
    case 'shipping':
      return validateShippingStep(nextStep)
    case 'delivery':
      return validateDeliveryStep(nextStep)
    case 'payment':
      return validatePaymentStep(nextStep)
    case 'review':
      return !nextStep ? validateReviewStep() : []
    default:
      return []
  }
}

export const getRemainingTotal = (
  // TODO - compare to duplicate function in payment-section.js, maybe eliminate that other one
  deliveryComplete = true,
  prevTotal = null,
  isCheckout = false,
  applyFinance = true,
  shippingComplete = true,
) => {
  const order = getOrder()
  let total = 0
  let appliedGiftCards = 0
  let appliedFinance = 0
  let tax = order ? order?.tax ?? 0 : 0
  if (prevTotal) {
    total = prevTotal
  }
  if (order && order.total && shippingComplete && isCheckout) {
    total = order.total
    if (!deliveryComplete && !order.paymentInfo.length && order.tax) {
      tax = 0
      total -= order.tax
    }
    if (deliveryComplete) {
      if (order.paymentInfo && order.paymentInfo.length > 0) {
        appliedGiftCards = order.paymentInfo
          .filter(payment => payment.paymentType === 'GIFT')
          .reduce((sum, gc) => sum + gc.authorizedAmount, 0)

        if (appliedGiftCards > 0) {
          total -= appliedGiftCards
        }

        const rtgFin = order.paymentInfo.filter(p => ['DBUY', 'FIN', 'GEN'].includes(p.paymentType))
        if (rtgFin.length > 0 && applyFinance) {
          appliedFinance = rtgFin[0].authorizedAmount
          total -= appliedFinance
        }
      }
    }
  }
  return {
    total,
    gift: appliedGiftCards,
    fin: appliedFinance,
    tax,
    deliveryCost: order && order.totalDeliveryCharge,
    totalSavings: order && order.promotions && order.promotions.totalSavings,
  }
}

export const clearCheckoutState = (
  clearOrderId = false,
  persistStoreCartLineItems = false,
  preventUpdateLineItems = false,
  preventStoreCart = false,
) => {
  const { contact, financePlan, lineItems, orderId, shippingAddress } = getOrder()

  const newOrder = {
    ...initialState.order,
    contact,
    financePlan: persistStoreCartLineItems ? { ...financePlan } : { ...initialState.order.financePlan },
    lineItems: persistStoreCartLineItems ? lineItems?.filter(item => item.isStoreSku) ?? [] : [],
    orderId: clearOrderId ? null : orderId,
    shippingAddress,
    tax: 0,
    total: 0,
    totalDeliveryCharge: 0,
    ...(!preventStoreCart && { storeCart: null }),
  }

  const location = getCurrentLocation()
  if (!preventUpdateLineItems) {
    updateLineItems({
      orderId: newOrder.orderId,
      lineItems: newOrder.lineItems,
      region: location.region,
      zone: parseInt(location.price_zone),
      distribution_index: parseInt(location.distribution_index),
    })
  }
  store.dispatch(setOrder(newOrder))
  store.dispatch(setCheckoutStepRedux('shipping'))
}

export const resetOrderAndCart = () => {
  store.dispatch(clearCart())
  store.dispatch(clearOrder())
}

export const removeMultiTenderPayment = (removeIndices, paymentInfo) => {
  const newPaymentInfo = paymentInfo
    .map(payment => {
      if (payment?.paymentType === 'CYBERV4') {
        let newPaymentProperties = payment.paymentProperties.filter(
          ({ card }) => !removeIndices.includes(card.appliedIndex),
        )
        if (newPaymentProperties.length === 0) return null

        // need to reassign the appliedIndex values for each remaining CC payments:
        newPaymentProperties = newPaymentProperties.map((p, i) => ({ ...p, card: { ...p.card, appliedIndex: i } }))

        return { paymentType: 'CYBERV4', paymentProperties: newPaymentProperties }
      }
      return payment
    })
    .filter(x => x) // to filter out any null values

  return newPaymentInfo ?? []
}

/**
 * Closes DeclineModal by resetting redux variable checkout.declineModalInfo to its initial state,
 * removes any existing payments, and returns user to Payment step. For CYBERV4 Multi-tender payment
 * errors, the payments with appliedIndex value included in indices will be removed.
 * @param {string} type
 * @param {string} pType
 * @param {array} indices
 */
export const setDeclineModalClose = (type, pType, indices) => {
  const order = getOrder()

  const newPaymentInfo = pType === 'CYBERV4' ? removeMultiTenderPayment(indices, order.paymentInfo) : []

  updatePayment({ orderId: order.orderId, paymentInfo: newPaymentInfo })
    .then(data => {
      data.selectedPaymentType = 'Credit'
      store.dispatch(setOrder(data))
      store.dispatch(setReviewInvalidFields([]))
      store.dispatch(setCheckoutStepRedux('payment'))
      scrollTo('payment')
    })
    .catch(error => {
      console.error('Error updating payment after decline:', error)
    })
    .finally(() => {
      store.dispatch(setDeclineModalInfo({ ...initialState.declineModalInfo }))
    })
}

const isLineItemEqual = (lineItem1, lineItem2) => {
  if (lineItem1.sku === '83333333')
    return lineItem1.sku === lineItem2.sku && lineItem1.unitPrice === lineItem2.unitPrice
  if (lineItem1?.containerSku) return lineItem1.sku === lineItem2.sku && lineItem1.uid === lineItem2.uid
  return lineItem1.sku === lineItem2.sku
}

export const getLineItems = cartState => {
  const { region } = getRegionZone()
  const cart = cartState || getCart()
  const lineItems = []
  if (cart && cart.cartItems) {
    for (let i = 0, n = cart.cartItems.length; i < n; i++) {
      const bonusBuyMainProduct =
        cart.cartItems[i]?.product?.promotions?.[`${region}_0`] ||
        cart.cartItems[i]?.product?.promotions?.default_promotion
      const isBonusBuy =
        !!bonusBuyMainProduct?.target_skus?.length &&
        cart.cartItems?.some(cartItem =>
          bonusBuyMainProduct?.target_skus?.map(targetSku => targetSku?.sku).includes(cartItem?.sku),
        )
      const index = lineItems.findIndex(product => isLineItemEqual(product, cart.cartItems[i]))
      if (index > -1) {
        lineItems[index].quantity += cart.cartItems[i].quantity
      } else {
        const isConfigurable = !!cart.cartItems[i].product?.isConfigurable
        lineItems.push({
          uid: cart.cartItems[i].uid,
          sku: cart.cartItems[i].sku,
          quantity: parseInt(cart.cartItems[i].quantity),
          childItems: isConfigurable
            ? cart.cartItems[i].product?.childItems ?? []
            : getProductChildren(cart.cartItems[i]),
          // Container Logic - if Bonus Buy, Containers should be handled as regular package
          containerSku: cart.cartItems[i]?.containerSku && !isBonusBuy,
          ...(!isBonusBuy && {
            containerId: cart.cartItems[i]?.containerId,
            containerName: cart.cartItems[i]?.containerName,
            containerImage: cart.cartItems[i]?.containerImage,
          }),
          deliveryType: cart.cartItems[i].product?.delivery_type,
          title: cart.cartItems[i].product?.title,
          category: cart.cartItems[i].product?.category,
          unitPrice: cart.cartItems[i].product?.unitPrice || cart.cartItems[i].product?.price,
          warrantyEnabled: cart.cartItems[i].warrantyEnabled,
          addons:
            cart.cartItems[i].activeAddons &&
            cart.cartItems[i].activeAddons.map(addon => ({
              uid: addon.uid,
              sku: addon.sku,
              quantity: addon.quantity,
              required:
                (cart.cartItems[i].product.addon_items &&
                  cart.cartItems[i].product.addon_items.filter(addon_item => addon_item.sku === addon.sku)?.[0]
                    ?.addon_required) ??
                false,
              warrantyEnabled: addon.warrantyEnabled,
            })),
          isConfigurable,
          ...(isConfigurable // Backend needs the cartId and imageUrl included for ModularOne configurator imported items
            ? {
                cartId: cart.cartItems[i].product?.configuratorCartId ?? '',
                imageUrl: cart.cartItems[i].product?.primary_image ?? '',
              }
            : {}),
          ...(cart?.cartItems?.[i]?.additionalOption ? { additionalOption: cart.cartItems[i].additionalOption } : {}),
          ...(cart?.cartItems?.[i]?.associatedPackage
            ? { associatedPackage: cart.cartItems[i].associatedPackage }
            : {}),
        })
      }
      if (
        !cart.cartItems[i].activeAddons ||
        (cart.cartItems[i].activeAddons && cart.cartItems[i].activeAddons.length > 0)
      ) {
        const requiredAddons =
          cart.cartItems[i].product?.addon_items &&
          cart.cartItems[i].product.addon_items.filter(addon => addon.addon_required)
        if (requiredAddons) {
          for (let x = 0, y = requiredAddons.length; x < y; x++) {
            if (
              requiredAddons &&
              requiredAddons.length > 0 &&
              (!cart.cartItems[i].activeAddons ||
                (cart.cartItems[i].activeAddons &&
                  cart.cartItems[i].activeAddons.filter(addon => addon.sku === requiredAddons[x].sku).length < 1))
            ) {
              const foundIndex = lineItems.findIndex(product => product.sku === '99999925')
              if (foundIndex > -1) {
                lineItems[foundIndex].quantity += cart.cartItems[i].quantity * requiredAddons[x].quantity
              } else {
                lineItems.push({
                  sku: '99999925',
                  quantity: parseInt(cart.cartItems[i].quantity * requiredAddons[x].quantity),
                  deliveryType: 'D',
                  required: true,
                })
              }
            }
          }
        }
        if (cart.cartItems[i].activeAddons) {
          for (let x = 0, y = cart.cartItems[i].activeAddons.length; x < y; x++) {
            const foundIndex = lineItems.findIndex(product => product.sku === cart.cartItems[i].activeAddons[x].sku)
            if (foundIndex > -1) {
              lineItems[foundIndex].quantity += parseInt(
                cart.cartItems[i].quantity * cart.cartItems[i].activeAddons[x].quantity,
              )
            } else {
              lineItems.push({
                uid: cart.cartItems[i].activeAddons[x].uid,
                sku: cart.cartItems[i].activeAddons[x].sku,
                quantity: parseInt(cart.cartItems[i].quantity * cart.cartItems[i].activeAddons[x].quantity),
                deliveryType: cart.cartItems[i].activeAddons[x].delivery_type,
                required: true,
                title: cart.cartItems[i].activeAddons[x].title,
                category: cart.cartItems[i].activeAddons[x].category,
                warrantyEnabled: cart.cartItems[i].activeAddons[x].warrantyEnabled,
              })
            }
          }
        }
        // Add foundation selections to line items
        if (!_isEmpty(cart?.cartItems[i]?.selections)) {
          const { selections } = cart?.cartItems[i]
          const foundIndex = lineItems.findIndex(product => product.sku === selections.sku)
          if (foundIndex > -1) {
            lineItems[foundIndex].quantity += selections.quantity
          } else {
            lineItems.push({
              uid: selections.uid,
              sku: selections.sku,
              quantity: parseInt(cart.cartItems[i].quantity * selections.quantity),
              deliveryType: selections.delivery_type,
              required: true,
              title: selections.title,
              category: selections.category,
              warrantyEnabled: selections.warrantyEnabled,
              unitPrice: productPrice(selections),
            })
          }
        }
      }
    }
  }
  return lineItems
}

export const getProductChildren = item => {
  if (item?.containerSku) {
    return item.childItems
  }
  let children = item?.product?.package_skus_v2
  const childArr = []
  if (children) {
    const { region } = getRegionZone()
    children = children[region]
    if (children) {
      for (let i = 0, n = children.length; i < n; i++) {
        childArr.push({ ...children[i], quantity: parseInt(children[i].quantity) })
      }
    }
  }
  return childArr
}

export const checkManualAddress = async (order, billing = false) => {
  if (order.acceptManual) return []

  let invalidFields = []
  const { address1, address2, city, state, zip } = billing ? order.billingAddress : order.shippingAddress
  const addressString1 = `${address1.trim()}${address2.trim() ? ` ${address2.trim()}` : ''}`
  const addressString2 = `${city.trim()} ${state} ${zip.trim()}`
  const addressString = `${addressString1}, ${addressString2}`
  const manualAddressQAS = await fetchAddressLookup(addressString)

  if (manualAddressQAS) {
    const hasMatches = manualAddressQAS?.result?.suggestions?.length > 0 ?? false
    const badPlus4 = !hasMatches && zip.includes('-')
    invalidFields = await addressSuggestion({ badPlus4, billing, manualAddressQAS, order })
  } else {
    invalidFields = ['unable to verify']
  }

  return invalidFields
}

export const addressSuggestion = async ({ badPlus4, billing, manualAddressQAS, order }) => {
  let invalidFields = []
  const updatedOrder = { ...order }

  let suggestedGAID

  if (manualAddressQAS?.result?.suggestions?.[0].global_address_key) {
    suggestedGAID = manualAddressQAS?.result?.suggestions[0]?.global_address_key ?? ''
  }

  let addr
  if (suggestedGAID) {
    addr = await fetchAddressFormat(suggestedGAID)
  }

  if (manualAddressQAS?.result?.suggestions?.[0]) {
    const { address1, address2, city, state, zip } = billing ? order.billingAddress : order.shippingAddress
    const addr1AndAddr2 = `${address1.trim()} ${address2?.trim() ?? ''}`.trim().toLowerCase()
    const { text: suggestion } = manualAddressQAS.result.suggestions[0]
    const [suggestedAddress1, suggestedAddress2] = suggestion.toLowerCase().split(',')
    const [suggestedZip, suggestedState] = suggestedAddress2.split(' ').reverse()
    const suggestedCity = suggestedAddress2
      .split(' ')
      .reverse()
      .slice(2)
      .reverse()
      .join(' ')
      .trim()

    let manualAddressMatchesSuggestion = false

    if (addr) {
      manualAddressMatchesSuggestion = Boolean(
        addr.address1.toLowerCase() === address1.trim().toLowerCase() &&
          addr.address2.toLowerCase() === address2.trim().toLowerCase() &&
          addr.city.toLowerCase() === city.trim().toLowerCase() &&
          addr.state.toLowerCase() === state.toLowerCase() &&
          addr.zip.toLowerCase() === zip.trim().toLowerCase(),
      )
    } else {
      manualAddressMatchesSuggestion = Boolean(
        suggestedAddress1 === addr1AndAddr2 &&
          suggestedCity === city.trim().toLowerCase() &&
          suggestedState === state.toLowerCase() &&
          suggestedZip === zip.trim().toLowerCase() &&
          !badPlus4,
      )
    }

    if (!manualAddressMatchesSuggestion) {
      invalidFields = ['unable to verify']
      updatedOrder.suggestedAddress = billing
        ? suggestion
        : {
            suggestedAddress: suggestion,
            formattedAddress: {
              addressLookupSuccess: true,
              address1: addr?.address1 ?? suggestedAddress1,
              address2: addr?.address2 ?? '',
              city: addr?.city ?? suggestedCity,
              globalAddressId: suggestedGAID,
              state: addr?.state ?? suggestedState,
              zip: addr?.zip ?? suggestedZip,
            },
          }
      store.dispatch(setOrder(updatedOrder))
    } else if (!billing) {
      // if the manual address does match suggestion for billing, do nothing, but for shipping need to update globalAddressId and addressLookupSuccess
      updatedOrder.shippingAddress = {
        ...updatedOrder.shippingAddress,
        globalAddressId: suggestedGAID,
        addressLookupSuccess: true,
      }
      store.dispatch(setOrder(updatedOrder))
    }
  } else {
    invalidFields = ['unable to verify']
  }
  return invalidFields
}

export const closeSuggestionModal = () => {
  const order = getOrder()
  store.dispatch(setOrder({ ...order, suggestedAddress: null }))
  store.dispatch(setShippingInvalidFields([]))
}

export const acceptAddressSuggestion = (suggestedAddress, setBillingState = null, formattedSuggestion) => {
  const isBilling = !!setBillingState
  setAddress(suggestedAddress, isBilling, formattedSuggestion)
  if (isBilling) {
    submitBillingAddress(setBillingState)
  } else {
    closeSuggestionModal()
    setCheckoutStep(null, 'shipping', 'delivery')
  }
}

export const declineAddressSuggestion = (setBillingState = null) => {
  const isBilling = !!setBillingState
  const order = getOrder()
  store.dispatch(setOrder({ ...order, acceptManual: true })) // do we need to distinguish between billing and shipping for acceptManual?
  if (isBilling) {
    submitBillingAddress(setBillingState)
  } else {
    closeSuggestionModal()
    setCheckoutStep(null, 'shipping', 'delivery')
  }
}

export const isGiftCardSelected = cart =>
  cart?.cartItems?.some(
    cartItem => cartItem.product?.title === 'Gift Card' && cartItem.product?.category === 'gift-card',
  )

/**
 * Determines if we are able to offer Klarna as a payment option
 *    • The subtotal must be > $35 AFTER a gift card is applied
 *    • The subtotal must be < $10000 AFTER a gift card is applied
 *    • If the subtotal is < $35 the Klarna badge should be inactive on the page
 *    • If cart includes a gift card, return false since Klarna cannot be used to buy gift cards
 * @param {object} order
 * @param {object} cart
 * @returns {boolean}
 */
export const getKlarnaAvailability = (order, cart) => {
  if (cart && isGiftCardSelected(cart)) {
    return false
  }
  const { total, paymentInfo, amountDue } = order
  if (Object.keys(paymentInfo).length === 0 && (amountDue || total) < 35) return false
  if (Object.keys(paymentInfo).length === 0 && (amountDue || total) > 10000) return false

  const giftCardTotal = paymentInfo.filter(p => p.paymentType === 'GIFT').reduce((a, i) => a + i.authorizedAmount, 0)
  if (Object.keys(paymentInfo).length > 0 && (total - giftCardTotal < 35 || total - giftCardTotal > 10000)) return false

  return true
}
